import { Object3D, Box3, Vector3 } from "three";
import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import CanvasInputListener from "../logic/CanvasInputListener";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import GeometryCommand from "./GeometryCommand";
import ChecklistItemData from "../model/sceneObjectData/ChecklistItemData";
import RaycastIntersection from "../logic/RaycastIntersection";
import createSceneObject from "../model/sceneObject/SceneObjectFactory";
import ISceneObject from "../model/sceneObject/ISceneObject";
import { useDialogStore } from "../store/dialogStore";
import { downloadScreenshot, captureScreenshotBlob } from "../utils/screenshotUtils";
import { useServicesState } from "../store/servicesStateStore";
import { getParentThreeProductByType } from "../utils/productUserDataUtils";
import { ElementTypeEnum } from "../elementTypeMappings";
import { SceneManager } from "../model/SceneManager";
import { useCameraControlsStore } from "../store/cameraControlsStore";
import { useModelPropertiesStore } from "../store/modelPropertiesStore/modelPropertiesStore";
import { sceneUnitsFromMm } from "../utils/scaler";
import { useViewerStateStore } from "../store/viewerStateStore";
import { StageTypeEnum } from "../store/viewerState/ViewerMode/ViewerMode";


enum AddChecklistItemCmdState {
    SETTING_POSITION,
    CONFIRMING_POSITION,
    FINISHED
}

export default class AddChecklistItemCmd extends GeometryCommand {
    cmdState: AddChecklistItemCmdState = AddChecklistItemCmdState.SETTING_POSITION;
    data: ChecklistItemData | undefined;
    sceneObject: ISceneObject | null = null;
    private previewParent: Object3D | null = null;
    private confirmed: boolean = false;

    constructor(commandStack: CommandStack, canvasInputListener: CanvasInputListener, document: Document) {
        super(CommandIdEnum.AddChecklistItem, commandStack, canvasInputListener, document);
        this.commandNameResourceKey = 'command.addChecklistItem.title';
        this.initialPrompResourceKey = 'command.addChecklistItem.setPosition';
        
        // Add checklist item is usually lauched from external app, such as CoreClient
        // which gives user the instructions to add a checklist item, 
        // so we don't need to show the command info message in this case.
        this.showCommandInfo = false; 
    }

    start() {
        super.start();
        super.addToStack();
        this.activate();
    }

    activate() {
        this.connectMouse();
    }

    connectMouse() {
        super.connectMouse();
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        this.canvasInputListener.resetTargetObjects();
        this.canvasInputListener.notifyPointerMoveAtCurrentPosition();
    }

    getObject(): ISceneObject {
        if (!this.sceneObject) {
            if (!this.data) {
                throw new Error("Data for AddChecklistItemCmd is not set");
            }
            this.sceneObject = createSceneObject(this.data);
            this.addToDrawables(this.sceneObject);
        }
        return this.sceneObject;
    }

    /**
     * Centers the camera on the placed preview and zooms so the item occupies
     * roughly 10 % of each linear screen dimension before the confirm dialog
     * appears.  Mirrors the FocusOnSceneObjectCmd pattern.
     */
    private focusCameraOnPreview(): void {
        if (!this.sceneObject) return;

        if (useViewerStateStore.getState().viewerMode?.stageType !== StageTypeEnum.Stage2d) return;

        const controls = useCameraControlsStore.getState().controls;
        if (!controls) return;

        const bbox = new Box3().setFromObject(this.sceneObject.geometry.group);
        if (bbox.isEmpty()) return;

        const size = new Vector3();
        bbox.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        // item / (item + 2 * padding) = 0.10  →  padding = maxDim * 4.5
        const padding = maxDim * 4.5;

        const center = bbox.getCenter(new Vector3());
        // Snap to top-down orientation first (no animation) so fitToBox starts
        // from a consistent position, then animate the zoom.
        controls.setLookAt(
            center.x, center.y, center.z + sceneUnitsFromMm(10000),
            center.x, center.y, center.z,
            false
        );

        controls.fitToBox(bbox, true, {
            paddingLeft:   padding,
            paddingRight:  padding,
            paddingTop:    padding,
            paddingBottom: padding,
        });

        const rotationOffset = useModelPropertiesStore.getState().rotationOffset;
        if (rotationOffset) {
            controls.rotate(rotationOffset, 0, false);
        }
    }

    /**
     * Returns the IFC parent (storey or building) for the given hit object,
     * or null when no suitable parent exists.
     */
    private findIFCParent(hitObject: Object3D | null): Object3D | null {
        if (!hitObject) return null;
        return getParentThreeProductByType(hitObject, ElementTypeEnum.Storey)
            ?? getParentThreeProductByType(hitObject, ElementTypeEnum.Building);
    }

    /**
     * Attaches the preview geometry to the given IFC parent so it inherits the
     * same coordinate-system rotation as confirmed checklist items, eliminating
     * the visual CCW offset between the preview and already-placed items.
     * Reparents only when the parent actually changes.
     */
    private syncPreviewParent(newParent: Object3D | null): void {
        if (newParent === this.previewParent || !this.sceneObject) return;
        this.previewParent = newParent;
        if (newParent) {
            newParent.add(this.sceneObject.geometry.group);
        } else {
            SceneManager.scene.add(this.sceneObject.geometry.group);
        }
    }

    /**
     * Converts world-space data to the preview parent's local space.
     * Returns the original data unchanged when there is no IFC parent.
     */
    private toLocalCoords(worldData: ChecklistItemData): ChecklistItemData {
        if (!this.previewParent) return worldData;
        const local = worldData.clone();
        local.applyMatrix4(this.previewParent.matrixWorld.clone().invert());
        return local;
    }

    pointerMove(raycastResult: RaycastIntersection): boolean {
        if (!raycastResult) return false;

        if (this.cmdState !== AddChecklistItemCmdState.SETTING_POSITION) return false;

        if (!this.data) {
            this.data = new ChecklistItemData(
                raycastResult.point.clone(),
                null,
                undefined,
                undefined,
                raycastResult.normalInWcs?.clone() ?? undefined
            );
            this.getObject(); // Create scene object and register as preview drawable
        }

        // Keep this.data in world coordinates (used by persistEntity).
        this.data.position.copy(raycastResult.point);
        if (raycastResult.normalInWcs) {
            this.data.normal.copy(raycastResult.normalInWcs);
        }

        // Reparent preview to the IFC parent so it inherits the same rotation
        // as confirmed items — this removes the apparent CCW offset.
        this.syncPreviewParent(this.findIFCParent(raycastResult.object));
        this.getObject().geometry.update(this.toLocalCoords(this.data), false);

        return true;
    }

    point(raycastResult: RaycastIntersection): boolean {
        if (this.cmdState !== AddChecklistItemCmdState.SETTING_POSITION) return true;

        const point = raycastResult.point.clone();
        super.setParent(raycastResult.object);

        const data = new ChecklistItemData(
            point.clone(),
            null,
            undefined,
            undefined,
            raycastResult.normalInWcs?.clone() ?? undefined
        );
        this.data = data;
        // Render in the parent's local space (same as pointerMove) so the
        // preview position is stable while the confirmation dialog is open.
        this.getObject().geometry.update(this.toLocalCoords(data), false);

        this.disconnectMouse();
        this.cmdState = AddChecklistItemCmdState.CONFIRMING_POSITION;
        this.focusCameraOnPreview();

        const containerRect = document.querySelector(".mainPage")?.getBoundingClientRect();
        const containerWidth = containerRect?.width ?? window.innerWidth;
        const containerHeight = containerRect?.height ?? window.innerHeight;

        useDialogStore.getState().showDialog(
            { x: Math.round(containerWidth / 2 - 110), y: Math.round(containerHeight * 2 / 3 - 60) },
            'command.addChecklistItem.confirmPosition.title',
            'command.addChecklistItem.confirmPosition.text',
            [
                {
                    optionNameTranslationKey: 'common.yes',
                    optionColor: '#27ae60',
                    optionFunction: async () => {
                        const saved = await this.persistEntity(data);
                        if (!saved) {
                            console.error("Failed to save checklist item. Please try again.");
                            return;
                        }
                        const filename = `checklist-item-${Date.now()}.png`;
                        //downloadScreenshot(filename);
                        let imageUrl: string | null = null;
                        if (this.lastPersistedEntityUuid) {
                            const blob = await captureScreenshotBlob();
                            if (blob) {
                                imageUrl = await useServicesState.getState().dataProvider
                                    ?.uploadChecklistItemImage(this.lastPersistedEntityUuid, blob, filename) ?? null;
                            }
                        }
                        window.parent.postMessage(
                            { type: 'CHECKLIST-ITEM-ADDED', data: { uuid: this.lastPersistedEntityUuid, imageUrl } },
                            '*'
                        );
                        this.confirmed = true;
                        this.end();
                    }
                },
                {
                    optionNameTranslationKey: 'common.no',
                    optionColor: '#e74c3c',
                    optionFunction: () => {
                        if (this.sceneObject) {
                            this.removeFromDrawables(this.sceneObject);
                        }
                        this.sceneObject = null;
                        this.data = undefined;
                        this.previewParent = null;
                        this.cmdState = AddChecklistItemCmdState.SETTING_POSITION;
                        this.connectMouse();
                        this.showInfoMessage('command.addChecklistItem.setPosition');
                    }
                },
            ]
        );

        return true;
    }

    end() {
        if (!this.confirmed) {
            window.parent.postMessage({ type: 'CHECKLIST-ITEM-ADD-ABORTED', data: {} }, '*');
        }
        super.end();
        super.removeFromStack();
        this.data = undefined;
        this.sceneObject = null;
        this.previewParent = null;
    }

    onViewerModeChange(): void {
        // Do not stop this command on viewer mode change
        // since user might want to change viewer mode before placing the checklist item
    }
}
